import { NavLink, useLocation } from 'react-router-dom'
import {
  Home,
  Users,
  Calendar,
  FileText,
  X,
  Activity,
  Database,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { cn } from '@/utils/cn'

interface SidebarProps {
  onClose?: () => void
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'My Clients', href: '/clients', icon: Users },
  { name: 'Programs', href: '/programs', icon: FileText },
  { name: 'Library', href: '/library', icon: Database },
  { name: 'Schedule', href: '/schedule', icon: Calendar },
]

export function Sidebar({ onClose, isCollapsed = false, onToggleCollapse }: SidebarProps) {
  const location = useLocation()

  return (
    <div className={cn(
      "flex flex-col h-full bg-white border-r border-gray-200 transition-all duration-300",
      isCollapsed ? "w-16" : "w-64"
    )}>
      {/* Logo and Controls */}
      <div className="flex items-center justify-between h-16 px-3 border-b border-gray-200">
        {!isCollapsed && (
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-primary-600 rounded-lg">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-gray-900">Fit Coach</span>
          </div>
        )}
        
        {isCollapsed && (
          <div className="flex items-center justify-center w-full">
            <div className="flex items-center justify-center w-8 h-8 bg-primary-600 rounded-lg">
              <Activity className="w-5 h-5 text-white" />
            </div>
          </div>
        )}

        <div className="flex items-center space-x-2">
          {/* Collapse Toggle - Hidden on mobile */}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="hidden lg:flex p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <ChevronLeft className="w-4 h-4" />
              )}
            </button>
          )}
          
          {/* Mobile Close Button */}
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 lg:hidden"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className={cn(
        "flex-1 py-6 space-y-1",
        isCollapsed ? "px-2" : "px-4"
      )}>
        <div className="space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.href || 
                           (item.href !== '/dashboard' && location.pathname.startsWith(item.href))
            
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={onClose}
                className={cn(
                  'group flex items-center text-sm font-medium rounded-lg transition-colors',
                  isCollapsed ? 'px-2 py-3 justify-center' : 'px-3 py-2',
                  isActive
                    ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                )}
                title={isCollapsed ? item.name : undefined}
              >
                <Icon 
                  className={cn(
                    'h-5 w-5 flex-shrink-0',
                    isCollapsed ? '' : 'mr-3',
                    isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                  )} 
                />
                {!isCollapsed && (
                  <span>{item.name}</span>
                )}
              </NavLink>
            )
          })}
        </div>
      </nav>
    </div>
  )
}